import { computed } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { isAxiosError } from 'axios';

import { useUserStore } from '@/shared/model';

import { watchlistApi } from '../api';
import type { WatchlistQueryData } from '../api';
import type {
  AddToWatchlistPayload,
  WatchlistItem,
  WatchlistToggleResult,
} from '../types';

export const WATCHLIST_QUERY_KEY = ['watchlist'] as const;

function isWatchlistLimitError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const errors = error.response?.data?.errors;
  return (
    Array.isArray(errors) &&
    errors.some(
      (apiError: { message?: string }) =>
        apiError.message === 'Watchlist limit reached',
    )
  );
}

export function useWatchlist() {
  const queryClient = useQueryClient();
  const userStore = useUserStore();

  const isAuthorized = computed(() => userStore.user !== null);

  const watchlistQuery = useQuery({
    queryKey: WATCHLIST_QUERY_KEY,
    queryFn: watchlistApi.getWatchlist,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthorized,
  });

  const addMutation = useMutation({
    mutationFn: (payload: AddToWatchlistPayload) =>
      watchlistApi.addToWatchlist(payload),
    onMutate: async newItem => {
      await queryClient.cancelQueries({ queryKey: WATCHLIST_QUERY_KEY });
      const previous = queryClient.getQueryData(WATCHLIST_QUERY_KEY);
      queryClient.setQueryData(WATCHLIST_QUERY_KEY, old => {
        const prevData = old as WatchlistQueryData | undefined;
        const items = prevData?.items ?? (Array.isArray(old) ? old : []) ?? [];
        return {
          items: [...items, { id: -Date.now(), ...newItem } as WatchlistItem],
          dict: prevData?.dict,
        };
      });
      return { previous };
    },
    onError: (_err, _newItem, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(WATCHLIST_QUERY_KEY, context.previous);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: WATCHLIST_QUERY_KEY }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string | number) => watchlistApi.removeFromWatchlist(id),
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey: WATCHLIST_QUERY_KEY });
      const previous = queryClient.getQueryData(WATCHLIST_QUERY_KEY);
      queryClient.setQueryData(WATCHLIST_QUERY_KEY, old => {
        const prevData = old as WatchlistQueryData | undefined;
        const items = prevData?.items ?? (Array.isArray(old) ? old : []) ?? [];
        return {
          items: items.filter(watchlistItem => watchlistItem.id !== id),
          dict: prevData?.dict,
        };
      });
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(WATCHLIST_QUERY_KEY, context.previous);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: WATCHLIST_QUERY_KEY }),
  });

  const items = computed(() => {
    const data = watchlistQuery.data.value;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.items ?? [];
  });

  const watchlistDict = computed(() => {
    const data = watchlistQuery.data.value;
    if (!data || Array.isArray(data)) return undefined;
    return data.dict;
  });

  const offerByHash = computed(
    () =>
      new Map(
        items.value
          .filter(
            (
              watchlistItem,
            ): watchlistItem is WatchlistItem & { hash: string } =>
              watchlistItem.hash !== null,
          )
          .map(watchlistItem => [watchlistItem.hash, watchlistItem]),
      ),
  );

  const itemBySearchQuery = computed(
    () =>
      new Map(
        items.value
          .filter(watchlistItem => watchlistItem.hash === null)
          .map(watchlistItem => [watchlistItem.search_query, watchlistItem]),
      ),
  );

  const isMutating = computed(
    () => addMutation.isLoading.value || removeMutation.isLoading.value,
  );

  const favoriteSearchQueries = computed(
    () => new Set(itemBySearchQuery.value.keys()),
  );

  const favoriteHashes = computed(() => new Set(offerByHash.value.keys()));

  async function toggleSearch(
    searchQuery: string,
    price: string,
    currency: string,
  ): Promise<WatchlistToggleResult> {
    if (!isAuthorized.value || isMutating.value) {
      return 'skipped';
    }
    const existing = itemBySearchQuery.value.get(searchQuery);
    try {
      if (existing) {
        if (typeof existing.id === 'number' && existing.id < 0)
          return 'skipped';
        await removeMutation.mutateAsync(existing.id);
        return 'removed';
      }
      await addMutation.mutateAsync({
        hash: null,
        search_query: searchQuery,
        initial_price: price,
        currency,
      });
      return 'added';
    } catch (error) {
      if (isWatchlistLimitError(error)) return 'limit_reached';
      return 'skipped';
    }
  }

  async function toggleOffer(
    hash: string,
    searchQuery: string,
    price: string,
    currency: string,
  ): Promise<WatchlistToggleResult> {
    if (!isAuthorized.value || isMutating.value) {
      return 'skipped';
    }
    const existing = offerByHash.value.get(hash);
    try {
      if (existing) {
        if (typeof existing.id === 'number' && existing.id < 0)
          return 'skipped';
        await removeMutation.mutateAsync(existing.id);
        return 'removed';
      }
      await addMutation.mutateAsync({
        hash,
        search_query: searchQuery,
        initial_price: price,
        currency,
      });
      return 'added';
    } catch (error) {
      if (isWatchlistLimitError(error)) return 'limit_reached';
      return 'skipped';
    }
  }

  return {
    items,
    watchlistDict,
    favoriteSearchQueries,
    favoriteHashes,
    isLoading: watchlistQuery.isLoading,
    /** True while watchlist query is fetching (initial load or refetch). */
    isWatchlistFetching: watchlistQuery.isFetching,
    isPending: addMutation.isLoading,
    /** True while add or remove mutation is in flight. */
    isMutating,
    /** Whether the user is authorized (watchlist requires auth). */
    isAuthorized,
    toggleSearch,
    toggleOffer,
  };
}
